/** Small shared helpers for the combat subsystem. */
import type { Affects, SimContext, SimEventInput, Unit, UnitId, Vec2 } from '../contracts';

/** Proc / nested-effect recursion cap (EffectCtx.depth). */
export const MAX_EFFECT_DEPTH = 4;
/** Recent-damage history kept on units (Amount.pctRecentDamageTaken windows must fit in it). */
export const RECENT_DAMAGE_KEEP_MS = 10000;
/** Grip pull speed (yards/sec). */
export const GRIP_SPEED = 40;

export const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
export const clamp01 = (v: number): number => clamp(v, 0, 1);

export function unitOf(ctx: SimContext, id: UnitId | null | undefined): Unit | undefined {
  return id ? ctx.world.units[id] : undefined;
}

export function isHostile(a: Unit | undefined, b: Unit | undefined): boolean {
  return !!a && !!b && a.faction !== b.faction;
}

/** Does `u` match an Affects filter relative to `src` (missing source matches everything). */
export function affectsMatch(src: Unit | undefined, u: Unit, affects: Affects): boolean {
  if (!src) return true;
  switch (affects) {
    case 'all':
      return true;
    case 'enemies':
      return u.faction !== src.faction;
    case 'allies':
      return u.faction === src.faction;
    case 'allies_not_self':
      return u.faction === src.faction && u.id !== src.id;
  }
}

export function dirBetween(from: Vec2, to: Vec2, fallbackAngle: number): Vec2 {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const d = Math.hypot(dx, dy);
  if (d < 1e-6) return { x: Math.cos(fallbackAngle), y: Math.sin(fallbackAngle) };
  return { x: dx / d, y: dy / d };
}

/**
 * Emit an event, dropping keys whose value is undefined so the World / log stay
 * JSON-clean (no undefined-vs-missing semantics).
 */
export function emitClean(ctx: SimContext, e: SimEventInput): void {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(e)) if (v !== undefined) out[k] = v;
  ctx.emit(out as SimEventInput);
}
